import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Smartphone, X, Download, Share2, Plus, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { trackInstallEvent } from '../../lib/analytics';
import { isNativeApp } from '../../lib/platform';

/**
 * InstallAppPrompt — Detects device (iOS Safari / Android Chrome / Desktop / Installed)
 * and shows the right install instructions. Dismissible with 7-day memory.
 *
 * variant: 'banner' (fixed strip, default) | 'card' (inline card with full steps)
 */
export default function InstallAppPrompt({ variant = 'banner', className = '' }) {
  const [device, setDevice] = useState(null); // 'ios' | 'android' | 'desktop' | 'installed' | null
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const key = 'install_prompt_dismissed_at';
      const v = localStorage.getItem(key);
      if (!v) return false;
      const days = (Date.now() - parseInt(v, 10)) / (1000 * 60 * 60 * 24);
      return days < 7;
    } catch { return false; }
  });
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => {
    // Already installed?
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    if (isStandalone) { setDevice('installed'); return; }

    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);

    if (isIOS) setDevice('ios');
    else if (isAndroid) setDevice('android');
    else setDevice('desktop');

    // Android/Chrome beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Fire once when the user actually completes install (Android + some desktop browsers)
    const installed = () => {
      trackInstallEvent('install', { page: window.location.pathname.split('/')[1] || 'landing' });
      setDevice('installed');
    };
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    try { localStorage.setItem('install_prompt_dismissed_at', String(Date.now())); } catch {}
    setDismissed(true);
  };

  // Don't show: already installed, dismissed, or device not detected yet
  if (isNativeApp() || device === 'installed' || device === null || dismissed) return null;

  // ==================== BANNER VARIANT (default) ====================
  if (variant === 'banner') {
    return (
      <>
        <div
          className={cn(
            "bg-gradient-to-r from-green-700 to-green-800 text-white rounded-xl shadow-lg px-3 py-3 flex items-center gap-3",
            className
          )}
          data-testid="install-prompt-banner"
        >
          <div className="shrink-0 w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
            <Smartphone size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight">Install Diamond Scorer</p>
            <p className="text-green-100 text-xs leading-snug">
              {device === 'ios' && 'Tap Share → Add to Home Screen'}
              {device === 'android' && 'One-tap install for push alerts'}
              {device === 'desktop' && 'Add to dock / taskbar'}
            </p>
          </div>
          {device === 'android' && deferredPrompt ? (
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-white text-green-800 hover:bg-green-50 font-bold shrink-0"
              data-testid="install-prompt-android-btn"
            >
              <Download size={14} className="mr-1" /> Install
            </Button>
          ) : device === 'ios' ? (
            <Button
              size="sm"
              onClick={() => setShowIosSteps(true)}
              className="bg-white text-green-800 hover:bg-green-50 font-bold shrink-0"
              data-testid="install-prompt-ios-btn"
            >
              Show me how
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowIosSteps(true)}
              className="bg-white text-green-800 hover:bg-green-50 font-bold shrink-0"
              data-testid="install-prompt-desktop-btn"
            >
              How?
            </Button>
          )}
          <button
            className="shrink-0 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center"
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="install-prompt-dismiss"
          >
            <X size={14} />
          </button>
        </div>

        {showIosSteps && <InstallStepsModal device={device} onClose={() => setShowIosSteps(false)} />}
      </>
    );
  }

  // ==================== CARD VARIANT (inline steps) ====================
  return (
    <div className={cn("bg-stone-900 border border-stone-800 rounded-xl p-4 text-white", className)} data-testid="install-prompt-card">
      <div className="flex items-center gap-3 mb-3">
        <Smartphone size={20} className="text-amber-400" />
        <div className="flex-1">
          <p className="font-bold">Install Diamond Scorer</p>
          <p className="text-stone-400 text-xs">Get push alerts + full-screen experience</p>
        </div>
        <button
          className="text-stone-500 hover:text-white w-7 h-7 rounded-full flex items-center justify-center"
          onClick={dismiss}
          data-testid="install-prompt-card-dismiss"
        >
          <X size={14} />
        </button>
      </div>
      <InstructionSteps device={device} deferredPrompt={deferredPrompt} onInstallClick={handleInstall} />
    </div>
  );
}

// ==================== Steps Modal ====================
function InstallStepsModal({ device, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="install-steps-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Smartphone size={20} className="text-green-700" />
            <h3 className="font-bold text-lg">Install Diamond Scorer</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="install-steps-close">
            <X size={18} />
          </button>
        </div>
        <InstructionSteps device={device} />
      </div>
    </div>
  );
}

// ==================== Shared Instructions ====================
function InstructionSteps({ device, deferredPrompt, onInstallClick }) {
  const commonStyle = "flex items-start gap-3 py-2";
  const stepNum = "shrink-0 w-6 h-6 rounded-full bg-green-600 text-white font-bold text-xs flex items-center justify-center";

  if (device === 'ios') {
    return (
      <div className="space-y-1 text-sm text-stone-700">
        <p className="text-[11px] text-amber-700 font-semibold bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 mb-2">
          ⚠️ Must be done in <strong>Safari</strong>, not Chrome
        </p>
        <div className={commonStyle}>
          <div className={stepNum}>1</div>
          <div>
            Tap the <strong className="inline-flex items-center gap-1 bg-stone-100 px-1.5 py-0.5 rounded"><Share2 size={12} /> Share</strong> icon at the bottom of Safari.
          </div>
        </div>
        <div className={commonStyle}>
          <div className={stepNum}>2</div>
          <div>
            Scroll down and tap <strong className="inline-flex items-center gap-1 bg-stone-100 px-1.5 py-0.5 rounded"><Plus size={12} /> Add to Home Screen</strong>.
          </div>
        </div>
        <div className={commonStyle}>
          <div className={stepNum}>3</div>
          <div>
            Tap <strong className="bg-stone-100 px-1.5 py-0.5 rounded">Add</strong> in the top-right. That&apos;s it!
          </div>
        </div>
        <p className="text-[11px] text-stone-500 pt-2">
          Once installed, you&apos;ll get push alerts for big plays (iOS 16.4+).
        </p>
      </div>
    );
  }

  if (device === 'android') {
    return (
      <div className="space-y-1 text-sm text-stone-700">
        {deferredPrompt ? (
          <>
            <p className="text-xs text-stone-600 mb-2">
              Chrome can install this instantly:
            </p>
            <Button
              onClick={onInstallClick}
              className="w-full bg-green-700 hover:bg-green-600 text-white font-bold"
              data-testid="install-steps-android-btn"
            >
              <Download size={14} className="mr-2" /> Install app now
            </Button>
            <p className="text-[11px] text-stone-500 pt-2 text-center">
              Or follow the manual steps below:
            </p>
          </>
        ) : null}
        <div className={commonStyle}>
          <div className={stepNum}>1</div>
          <div>
            Tap the <strong className="bg-stone-100 px-1.5 py-0.5 rounded">⋮ menu</strong> in the top-right of Chrome.
          </div>
        </div>
        <div className={commonStyle}>
          <div className={stepNum}>2</div>
          <div>
            Tap <strong className="bg-stone-100 px-1.5 py-0.5 rounded">Install app</strong> (or <em>Add to Home screen</em>).
          </div>
        </div>
        <div className={commonStyle}>
          <div className={stepNum}>3</div>
          <div>
            Confirm <strong className="bg-stone-100 px-1.5 py-0.5 rounded">Install</strong>. Done!
          </div>
        </div>
      </div>
    );
  }

  // Desktop
  return (
    <div className="space-y-1 text-sm text-stone-700">
      <div className={commonStyle}>
        <div className={stepNum}>1</div>
        <div>
          Look for the <strong className="inline-flex items-center gap-1 bg-stone-100 px-1.5 py-0.5 rounded"><Download size={12} /> install</strong> icon in Chrome / Edge&apos;s address bar.
        </div>
      </div>
      <div className={commonStyle}>
        <div className={stepNum}>2</div>
        <div>
          Click it, then click <strong className="bg-stone-100 px-1.5 py-0.5 rounded">Install</strong>.
        </div>
      </div>
      <div className={commonStyle}>
        <div className={stepNum}>3</div>
        <div>
          Diamond Scorer now lives in your dock / taskbar <ChevronRight size={12} className="inline" /> tap it like any native app.
        </div>
      </div>
    </div>
  );
}
